import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { getServiceById } from '../lib/servicesData';
import CtaBanner from '../components/home/CtaBanner';

const Check = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path d="M2.5 7.5l3.2 3.2 6.8-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceById(slug);
  const [openFaq, setOpenFaq] = useState(0);

  if (!service) return <Navigate to="/services" replace />;

  const pageTitle = `${service.title} Toronto | Nexgeius`;
  const pageUrl = `https://www.nexgeius.com/services/${service.id}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`${service.shortDesc} Starting at ${service.startingAt}. See the process, packages, and ongoing costs.`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={service.shortDesc} />
        <meta property="og:url" content={pageUrl} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: service.title,
          description: service.shortDesc,
          areaServed: { '@type': 'Country', name: 'Canada' },
          provider: { '@type': 'Organization', name: 'Nexgeius', url: 'https://www.nexgeius.com/' },
          url: pageUrl,
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: service.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: { '@type': 'Answer', text: faq.answer },
          })),
        })}</script>
      </Helmet>

      <main>
        <section className="page-grid bg-[#F6F8FA] px-6 pb-20 pt-28 lg:px-8 lg:pb-24 lg:pt-32">
          <div className="relative z-10 mx-auto max-w-7xl">
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-[#52636E] transition hover:text-[#176B8A]">
              <span aria-hidden="true">&larr;</span> All services
            </Link>
            <div className="mt-10 grid gap-10 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
              <div>
                <div className="service-icon flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ backgroundColor: service.accent }}>
                  {service.icon}
                </div>
                <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: service.accent }}>{service.eyebrow}</p>
                <h1 className="mt-4 max-w-4xl text-[clamp(2.75rem,6vw,5.7rem)] font-semibold leading-[0.96] tracking-[-0.055em] text-[#102A3A]">{service.title}</h1>
              </div>
              <div className="max-w-xl lg:justify-self-end">
                <p className="text-lg leading-8 text-[#52636E]">{service.intro}</p>
                <div className="mt-8 grid grid-cols-2 gap-4 border-t border-[#CBD8DE] pt-6">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.17em] text-[#82939C]">Projects start at</p>
                    <p className="mt-2 text-xl font-semibold text-[#102A3A]">{service.startingAt}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.17em] text-[#82939C]">Typical timeline</p>
                    <p className="mt-2 text-xl font-semibold text-[#102A3A]">{service.timeline}</p>
                  </div>
                </div>
                <Link to="/contact" className="button-primary mt-8 inline-flex items-center gap-2">Start a conversation <span aria-hidden="true">&rarr;</span></Link>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-[#DCE4E8] bg-white px-6 py-6 lg:px-8">
          <div className="mx-auto flex max-w-7xl flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#82939C]">Best for</p>
            <p className="max-w-4xl text-sm font-medium leading-6 text-[#253E4D] md:text-right">{service.bestFor}</p>
          </div>
        </section>

        <section className="bg-white px-6 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[.65fr_1.35fr]">
              <div>
                <p className="section-kicker">What the work covers</p>
                <h2 className="mt-4 max-w-sm text-3xl font-semibold tracking-[-0.035em] text-[#102A3A] md:text-4xl">Useful from day one. Ready for what comes next.</h2>
              </div>
              <div className="grid gap-px overflow-hidden rounded-2xl border border-[#DCE4E8] bg-[#DCE4E8] sm:grid-cols-2">
                {service.features.map((feature, index) => (
                  <article key={feature.title} className="bg-[#FBFCFD] p-7">
                    <span className="text-xs font-semibold tabular-nums text-[#8A9AA3]">0{index + 1}</span>
                    <h3 className="mt-8 text-xl font-semibold tracking-[-0.02em] text-[#102A3A]">{feature.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[#63747E]">{feature.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#F6F8FA] px-6 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="section-kicker">Ways to start</p>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A] md:text-4xl">A clear first scope, with no mystery line items.</h2>
              <p className="mt-4 text-base leading-7 text-[#63747E]">Prices are starting points in Canadian dollars, before tax and third-party services. The proposal confirms deliverables, exclusions, payment stages, and ownership.</p>
            </div>
            <div className="mt-10 grid gap-5 lg:grid-cols-3">
              {service.packages.map((offer, index) => (
                <article key={offer.name} className={`flex flex-col rounded-2xl border p-7 ${index === 1 ? 'border-[#8AB4C6] bg-white shadow-[0_18px_50px_rgba(16,42,58,0.08)]' : 'border-[#D5E0E5] bg-[#FBFCFD]'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-xl font-semibold text-[#102A3A]">{offer.name}</h3>
                    <span className="rounded-full bg-[#E6F0F4] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.13em] text-[#176B8A]">{index === 1 ? 'Popular start' : `0${index + 1}`}</span>
                  </div>
                  <p className="mt-6 text-2xl font-semibold tracking-[-0.025em] text-[#102A3A]">{offer.price}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.13em] text-[#82939C]">{offer.timeline}</p>
                  <p className="mt-5 text-sm leading-6 text-[#63747E]">{offer.description}</p>
                  <ul className="mt-7 space-y-3 border-t border-[#DCE4E8] pt-6">
                    {offer.items.map((item) => (
                      <li key={item} className="flex gap-3 text-sm leading-5 text-[#253E4D]">
                        <span className="mt-0.5 text-[#176B8A]"><Check /></span>{item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#102A3A] px-6 py-20 text-white lg:px-8 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[.65fr_1.35fr]">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#92C5D8]">How it happens</p>
                <h2 className="mt-4 max-w-sm text-3xl font-semibold tracking-[-0.035em] md:text-4xl">Visible progress from scope to launch.</h2>
              </div>
              <ol className="grid gap-px overflow-hidden rounded-2xl border border-white/15 bg-white/15 sm:grid-cols-2">
                {service.process.map((step, index) => (
                  <li key={step.title} className="bg-[#102A3A] p-7">
                    <span className="text-xs font-semibold text-[#92C5D8]">0{index + 1}</span>
                    <h3 className="mt-7 text-xl font-semibold">{step.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[#C5D3DA]">{step.description}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="bg-white px-6 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[.7fr_1.3fr]">
            <div>
              <p className="section-kicker">What keeps costing money</p>
              <h2 className="mt-4 max-w-md text-3xl font-semibold tracking-[-0.035em] text-[#102A3A] md:text-4xl">The build price is not the whole operating picture.</h2>
              <p className="mt-5 max-w-md text-sm leading-6 text-[#63747E]">We will list expected subscriptions and usage-based services in the proposal, then put accounts in your name wherever practical.</p>
            </div>
            <div className="divide-y divide-[#DCE4E8] border-y border-[#DCE4E8]">
              {service.ongoingCosts.map((cost) => (
                <div key={cost.label} className="grid gap-3 py-6 sm:grid-cols-[.35fr_.65fr]">
                  <h3 className="font-semibold text-[#102A3A]">{cost.label}</h3>
                  <p className="text-sm leading-6 text-[#63747E]">{cost.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#F6F8FA] px-6 py-20 lg:px-8">
          <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[.55fr_1fr]">
            <div>
              <p className="section-kicker">Common questions</p>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A]">Before we start.</h2>
            </div>
            <div className="divide-y divide-[#CBD8DE] border-y border-[#CBD8DE]">
              {service.faqs.map((faq, index) => {
                const isOpen = openFaq === index;
                return (
                  <div key={faq.question}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-5 py-6 text-left"
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                      aria-expanded={isOpen}
                    >
                      <span className="font-semibold leading-6 text-[#102A3A]">{faq.question}</span>
                      <span className="text-xl font-light text-[#176B8A]" aria-hidden="true">{isOpen ? '-' : '+'}</span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22 }}
                          className="overflow-hidden"
                        >
                          <p className="max-w-2xl pb-6 text-sm leading-7 text-[#63747E]">{faq.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <CtaBanner />
      </main>
    </>
  );
};

export default ServiceDetail;
